/**
 * 공통 API 클라이언트
 * 모든 서비스에서 사용하는 기본 HTTP 요청 래퍼
 */

import { readApiResponseError } from './companyDataAuthorizationService';

const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return `${protocol}//${hostname}:8001`;
  }
  return 'http://localhost:8001';
};

const TOKEN_KEY = 'proposalcraft_access_token';

export interface ApiError {
  status: number;
  message: string;
  detail?: string;
  code?: string;
}

export interface PaginatedResponse<T> {
  total: number;
  items: T[];
  page?: number;
  page_size?: number;
}

class ApiClient {
  private getHeaders(contentType?: string): HeadersInit {
    const token = localStorage.getItem(TOKEN_KEY);
    const headers: Record<string, string> = {};
    if (contentType) {
      headers['Content-Type'] = contentType;
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('proposalcraft_refresh_token');
      localStorage.removeItem('proposalcraft_user');
      window.location.href = '/login';
      throw { status: 401, message: '인증이 만료되었습니다. 다시 로그인해주세요.' } as ApiError;
    }

    if (!response.ok) {
      const parsed = await readApiResponseError(
        response,
        `HTTP ${response.status}: ${
          response.statusText || '요청 처리 중 오류가 발생했습니다.'
        }`,
      );
      throw parsed as ApiError;
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  async get<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    const url = new URL(`${getApiBaseUrl()}${path}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.getHeaders(),
    });
    return this.handleResponse<T>(response);
  }

  async post<T>(path: string, body?: Record<string, unknown> | FormData): Promise<T> {
    const isFormData = body instanceof FormData;
    const response = await fetch(`${getApiBaseUrl()}${path}`, {
      method: 'POST',
      headers: this.getHeaders(isFormData ? undefined : 'application/json'),
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse<T>(response);
  }

  async put<T>(path: string, body?: Record<string, unknown>): Promise<T> {
    const response = await fetch(`${getApiBaseUrl()}${path}`, {
      method: 'PUT',
      headers: this.getHeaders('application/json'),
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse<T>(response);
  }

  async delete<T>(path: string): Promise<T> {
    const response = await fetch(`${getApiBaseUrl()}${path}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse<T>(response);
  }

  async download(path: string, filename: string): Promise<void> {
    const response = await fetch(`${getApiBaseUrl()}${path}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });
    if (!response.ok) {
      throw { status: response.status, message: '파일 다운로드에 실패했습니다.' } as ApiError;
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const apiClient = new ApiClient();
export { getApiBaseUrl };
export default apiClient;
